<template>
    <div ref="formContainer" class="modern-form-container">
        <div class="form-header">
            <div class="header-content">
                <div class="title-section">
                    <div class="icon-wrapper">
                        <i class="fas fa-stethoscope"></i>
                    </div>
                    <div>
                        <h4 class="form-title">
                            {{ isEditing ? "Editar Nota Médica" : "Nueva Nota Médica" }}
                            <span v-if="isEditing && localNoteForm.id" class="id-badge">#{{ localNoteForm.id }}</span>
                        </h4>
                        <p class="form-subtitle">Registro de consulta y evolución del paciente</p>
                    </div>
                </div>
                <div class="action-buttons">
                    <ArgonButton
variant="gradient" color="success" :disabled="isSaving" size="md"
                        class="header-btn-primary" @click="handleSubmit">
                        <i :class="localNoteForm.id ? 'fas fa-save' : 'fas fa-plus-circle'" class="me-2"></i>
                        {{ isSaving ? 'Guardando...' : (localNoteForm.id ? 'Guardar Nota' : 'Crear Nota') }}
                    </ArgonButton>
                    <div v-if="isEditing" class="secondary-actions">
                        <ArgonButton
variant="gradient" color="primary" size="sm" class="header-btn-secondary pdf-btn"
                            :disabled="generatingPdf" data-bs-toggle="tooltip" title="Generar PDF de la nota"
                            @click="generateNotePdf">
                            <i v-if="!generatingPdf" class="fas fa-file-pdf"></i>
                            <i v-else class="fas fa-spinner fa-spin"></i>
                        </ArgonButton>
                        <ArgonButton
variant="gradient" color="info" size="sm" class="header-btn-secondary"
                            data-bs-toggle="tooltip" title="Duplicar nota" @click="handleDuplicateNote">
                            <i class="fas fa-copy"></i>
                        </ArgonButton>
                        <ArgonButton
variant="gradient" color="danger" size="sm" class="header-btn-secondary"
                            data-bs-toggle="tooltip" title="Eliminar nota" @click="deleteNote('reason_for_visit')">
                            <i class="fas fa-trash"></i>
                        </ArgonButton>
                    </div>
                </div>
            </div>
        </div>

        <!-- Barra de estado profesional -->
        <div class="status-bar">
            <div class="status-left">
                <ProgressIndicator :form-data="localNoteForm" :compact="true" />
            </div>

            <div class="status-center">
                <!-- Notificación de borrador inline -->
                <transition name="slide-fade">
                    <div v-if="hasDraft" class="draft-badge">
                        <i class="fas fa-file-alt"></i>
                        <span>Borrador disponible</span>
                        <button class="draft-btn-restore" title="Restaurar borrador" @click="handleRestoreDraft">
                            <i class="fas fa-undo"></i>
                        </button>
                        <button class="draft-btn-discard" title="Descartar borrador" @click="handleDiscardDraft">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                </transition>
            </div>

            <div class="status-right">
                <AutosaveIndicator
:last-save-time="lastSaveTime" :has-unsaved-changes="hasUnsavedChanges"
                    :has-draft="hasDraft" :formatted-save-time="getFormattedSaveTime()" :compact="true" :inline="true"
                    @restore-draft="handleRestoreDraft" @discard-draft="handleDiscardDraft" />
            </div>
        </div>

        <form class="modern-form" @submit.prevent="handleSubmit">
            <!-- Sección Principal -->
            <div class="form-section">
                <div class="section-title">
                    <i class="fas fa-clipboard-check"></i>
                    <span>Información de la Consulta</span>
                </div>

                <div class="form-group enhanced">
                    <label for="reasonForVisit" class="form-label modern">
                        <span class="label-content">
                            <i class="fas fa-question-circle"></i>
                            <span>Motivo de la consulta</span>
                        </span>
                        <span class="required-badge">Requerido</span>
                    </label>
                    <div class="input-wrapper">
                        <input
id="reasonForVisit" ref="reasonForVisitInput" v-model="localNoteForm.reason_for_visit"
                            name="reasonForVisit" type="text" class="form-control modern" :class="{
                                'is-invalid': errors.reason_for_visit,
                                'has-content': localNoteForm.reason_for_visit
                            }" placeholder="Ej: Dolor abdominal, fiebre, control rutinario..." />
                        <transition name="check-fade">
                            <div
v-if="localNoteForm.reason_for_visit && !errors.reason_for_visit"
                                class="success-indicator">
                                <i class="fas fa-check-circle"></i>
                            </div>
                        </transition>
                    </div>
                    <transition name="error-slide-down">
                        <div v-if="errors.reason_for_visit" class="error-message-modern">
                            <i class="fas fa-exclamation-triangle"></i>
                            <span>{{ getErrorMessage(errors.reason_for_visit, 'reason_for_visit') }}</span>
                        </div>
                    </transition>
                    <div v-if="!errors.reason_for_visit" class="help-text-modern">
                        <i class="fas fa-lightbulb"></i>
                        <span>Resume en una frase clara el motivo principal de la consulta (síntoma, control o estudio
                            solicitado).</span>
                    </div>
                </div>
            </div>

            <!-- Sección SOAP -->
            <div class="form-section soap-section">
                <div class="section-title enhanced">
                    <div class="title-left">
                        <i class="fas fa-notes-medical"></i>
                        <span>Evolución PSOAP</span>
                        <span class="required-badge">Requerido</span>
                        <span class="info-badge" title="Plan, Subjetivo, Objetivo, Análisis, Plan">
                            <i class="fas fa-info-circle"></i>
                        </span>
                    </div>
                </div>

                <div class="form-group enhanced">
                    <div class="editor-wrapper" :class="{ 'has-error': errors.soap }">
                        <quill-editor
v-model:value="localNoteForm.soap" :options="$editorOptions" class="editor modern"
                            @focus="activeEditor = 'soap'" @blur="activeEditor = null" />
                        <div class="editor-footer">
                            <div class="editor-stats">
                                <span class="stat-item">
                                    <i class="fas fa-font"></i>
                                    {{ getTextLength(localNoteForm.soap) }} caracteres
                                </span>
                                <span class="stat-item">
                                    <i class="fas fa-align-left"></i>
                                    {{ getWordCount(localNoteForm.soap) }} palabras
                                </span>
                            </div>
                        </div>
                    </div>
                    <transition name="error-slide-down">
                        <div v-if="errors.soap" class="error-message-modern">
                            <i class="fas fa-exclamation-triangle"></i>
                            <span>{{ getErrorMessage(errors.soap, 'soap') }}</span>
                        </div>
                    </transition>
                    <div v-if="!errors.soap && !localNoteForm.soap" class="help-text-modern">
                        <i class="fas fa-lightbulb"></i>
                        <span>Registra: Plan inicial, Síntomas Subjetivos, hallazgos Objetivos, Análisis diagnóstico y
                            Plan terapéutico</span>
                    </div>
                </div>
            </div>

            <!-- Sección Exploración Física -->
            <div class="form-section">
                <div class="section-title enhanced">
                    <div class="title-left">
                        <i class="fas fa-user-md"></i>
                        <span>Exploración Física</span>
                    </div>
                </div>

                <div class="form-group enhanced">
                    <div class="editor-wrapper" :class="{ 'has-error': errors.physical_examination }">
                        <quill-editor
v-model:value="localNoteForm.physical_examination" :options="$editorOptions"
                            class="editor modern" @focus="activeEditor = 'physical_examination'"
                            @blur="activeEditor = null" />
                        <div class="editor-footer">
                            <div class="editor-stats">
                                <span class="stat-item">
                                    <i class="fas fa-font"></i>
                                    {{ getTextLength(localNoteForm.physical_examination) }} caracteres
                                </span>
                            </div>
                        </div>
                    </div>
                    <transition name="error-slide-down">
                        <div v-if="errors.physical_examination" class="error-message-modern">
                            <i class="fas fa-exclamation-triangle"></i>
                            <span>{{ getErrorMessage(errors.physical_examination, 'physical_examination') }}</span>
                        </div>
                    </transition>
                    <div
v-if="!errors.physical_examination && !localNoteForm.physical_examination"
                        class="help-text-modern">
                        <i class="fas fa-lightbulb"></i>
                        <span>Describe los hallazgos de la exploración física general</span>
                    </div>
                </div>
            </div>

            <!-- Formulario de Exploración Física Detallada -->
            <div class="form-section">
                <PhysicalExaminationForm v-model="localNoteForm.physical_examination_json" />
            </div>

            <!-- 🤰 Control de Embarazo - Solo para pacientes mujeres y si está activo en HC -->
            <div v-if="isPacienteFemenino" class="form-section">
                <PregnancyControlNote
:patient-record="patientRecord"
                    :note-date="localNoteForm.created || new Date().toISOString().split('T')[0]" />
            </div>

            <!-- Sección Estudios -->
            <div class="form-section">
                <div class="section-title enhanced">
                    <div class="title-left">
                        <i class="fas fa-flask"></i>
                        <span>Estudios de Laboratorio y Gabinete</span>
                    </div>
                </div>

                <div class="form-group enhanced">
                    <div class="editor-wrapper" :class="{ 'has-error': errors.laboratory_and_studies }">
                        <quill-editor
v-model:value="localNoteForm.laboratory_and_studies" :options="$editorOptions"
                            class="editor modern" @focus="activeEditor = 'laboratory_and_studies'"
                            @blur="activeEditor = null" />
                        <div class="editor-footer">
                            <div class="editor-stats">
                                <span class="stat-item">
                                    <i class="fas fa-font"></i>
                                    {{ getTextLength(localNoteForm.laboratory_and_studies) }} caracteres
                                </span>
                            </div>
                        </div>
                    </div>
                    <transition name="error-slide-down">
                        <div v-if="errors.laboratory_and_studies" class="error-message-modern">
                            <i class="fas fa-exclamation-triangle"></i>
                            <span>{{ getErrorMessage(errors.laboratory_and_studies, 'laboratory_and_studies') }}</span>
                        </div>
                    </transition>
                    <div
v-if="!errors.laboratory_and_studies && !localNoteForm.laboratory_and_studies"
                        class="help-text-modern">
                        <i class="fas fa-lightbulb"></i>
                        <span>Detalla estudios solicitados, resultados y su interpretación clínica</span>
                    </div>
                </div>
            </div>

            <!-- Sección Diagnósticos y Problemas Clínicos -->
            <div class="form-section">
                <div class="section-title enhanced">
                    <div class="title-left">
                        <i class="fas fa-diagnoses"></i>
                        <span>Diagnósticos y Problemas Clínicos</span>
                    </div>
                </div>

                <div class="form-group enhanced">
                    <label class="form-label modern">
                        <span class="label-content">
                            <i class="fas fa-file-medical-alt"></i>
                            <span>Descripción de diagnósticos</span>
                        </span>
                    </label>
                    <div class="editor-wrapper" :class="{ 'has-error': errors.diagnoses_clinical_problems }">
                        <quill-editor
v-model:value="localNoteForm.diagnoses_clinical_problems"
                            :options="$editorOptions" class="editor modern"
                            @focus="activeEditor = 'diagnoses_clinical_problems'" @blur="activeEditor = null" />
                        <div class="editor-footer">
                            <div class="editor-stats">
                                <span class="stat-item">
                                    <i class="fas fa-font"></i>
                                    {{ getTextLength(localNoteForm.diagnoses_clinical_problems) }} caracteres
                                </span>
                            </div>
                        </div>
                    </div>
                    <transition name="error-slide-down">
                        <div v-if="errors.diagnoses_clinical_problems" class="error-message-modern">
                            <i class="fas fa-exclamation-triangle"></i>
                            <span>{{ getErrorMessage(errors.diagnoses_clinical_problems, 'diagnoses_clinical_problems')
                                }}</span>
                        </div>
                    </transition>
                    <div
v-if="!errors.diagnoses_clinical_problems && !localNoteForm.diagnoses_clinical_problems"
                        class="help-text-modern">
                        <i class="fas fa-lightbulb"></i>
                        <span>Describe los diagnósticos clínicos, problemas identificados y su gravedad</span>
                    </div>
                </div>
            </div>

            <!-- Sección Diagnósticos CIE-10 -->
            <div class="form-section">
                <div class="section-title">
                    <i class="fas fa-stethoscope"></i>
                    <span>Diagnósticos CIE-10</span>
                </div>
                <div class="diagnoses-grid">
                    <div class="grid-item">
                        <label class="form-label modern">
                            <span class="label-content">
                                <i class="fas fa-star"></i>
                                <span>Diagnóstico Principal</span>
                            </span>
                        </label>
                        <MenuSelectedFull
v-model="localNoteForm.diagnosis_cie10_1" :label="''" apiurl="cie10"
                            :can-edit-and-add="false" />
                    </div>
                    <div class="grid-item">
                        <label class="form-label modern">
                            <span class="label-content">
                                <i class="fas fa-plus"></i>
                                <span>Diagnóstico Secundario 1</span>
                            </span>
                        </label>
                        <MenuSelectedFull
v-model="localNoteForm.diagnosis_cie10_2" :label="''" apiurl="cie10"
                            :can-edit-and-add="false" />
                    </div>
                    <div class="grid-item">
                        <label class="form-label modern">
                            <span class="label-content">
                                <i class="fas fa-plus"></i>
                                <span>Diagnóstico Secundario 2</span>
                            </span>
                        </label>
                        <MenuSelectedFull
v-model="localNoteForm.diagnosis_cie10_3" :label="''" apiurl="cie10"
                            :can-edit-and-add="false" />
                    </div>
                </div>
            </div>

            <!-- Sección Metadata -->
            <div class="form-section">
                <div class="section-title">
                    <i class="fas fa-calendar-alt"></i>
                    <span>Información de la Consulta</span>
                </div>

                <div class="form-group">
                    <label class="form-label modern">
                        <span class="label-content">
                            <i class="fas fa-calendar-alt"></i>
                            <span>Fecha de creación</span>
                        </span>
                        <span class="required-badge">Requerido</span>
                    </label>
                    <DatePicker v-model="localNoteForm.created" :errors="errors" error-field-name="created" />
                </div>
            </div>
        </form>

        <!-- Navegación entre notas (solo en modo editar) -->
        <div v-if="isEditing && filteredNotes.length > 0" class="notes-navigation">
            <NotesFiltersButtons
:filtered-notes="filteredNotes" :current-id="currentId" :select-note="selectNoteForm"
                :format-date="formatDate" record-type="nota" />
        </div>

        <!-- Ayuda de atajos de teclado -->
        <KeyboardShortcutsHelp ref="keyboardHelpRef" :is-mac="isMac" />
    </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick, computed, getCurrentInstance } from 'vue';
import { useNoteFormGlobal } from '@/composables/useNoteFormGlobal';
import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts';
import { useAutosave } from '@/composables/useAutosave';
import api from '@/services/api';
// ✅ Imports absolutos corregidos
import NotesFiltersButtons from '@/components/NotesFiltersButtons.vue';
import DatePicker from "@/components/DatePicker.vue";
import MenuSelectedFull from '@/components/MenuSelectedFull.vue';
import PhysicalExaminationForm from '@/components/PhysicalExaminationForm.vue';
import ArgonButton from '@/components/ArgonButton.vue';
import KeyboardShortcutsHelp from '@/components/KeyboardShortcutsHelp.vue';
import AutosaveIndicator from '@/components/AutosaveIndicator.vue';
import ProgressIndicator from '@/components/ProgressIndicator.vue';
import PregnancyControlNote from '@/components/MedicalRecord/Forms/ClinicalHistory/PregnancyControlNote.vue';

import { quillEditor } from 'vue3-quill';

// Estado para loading
const isSaving = ref(false);
const activeEditor = ref(null);
const generatingPdf = ref(false);

// Referencias
const reasonForVisitInput = ref(null);
const formContainer = ref(null);

// Obtener proxy para notificaciones
const { proxy } = getCurrentInstance();

const props = defineProps({
    notes: {
        type: Array,
        default: () => [],
    },
    noteForm: {
        type: Object,
        default: () => ({
            physical_examination_json: '{}',
            created: new Date().toISOString().split('T')[0]
        })
    },
    currentId: {
        type: Number,
        default: 0,
        required: true,
    },
    selectNote: {
        type: Function,
        required: true,
    },
    errors: {
        type: Object,
        default: () => ({}),
    },
    patientRecord: {
        type: Object,
        required: true
    },
    activeTabPrefix: {
        type: String,
        required: true
    }
});

const emit = defineEmits({
    'save-note': null,
    'reset-form': null,
    'update:currentId': null,
    'delete-note': null,
    'update:activeTab': null,
    'clear-field-error': null,
    'clear-all-errors': null
});

// 👤 Computed para verificar si el paciente es femenino
const isPacienteFemenino = computed(() => {
    const gender = props.patientRecord?.gender;
    return gender === 'F' || gender === 'female';
});

const {
    localNoteForm,
    filteredNotes,
    handleSubmit: originalHandleSubmit,
    selectNoteForm,
    deleteNote,
    handleDuplicateNote,
    formatDate,
    isEditing
} = useNoteFormGlobal(props, emit, props.activeTabPrefix);

// Wrapper para handleSubmit sin re-focus para evitar parpadeo
const handleSubmit = async () => {
    await originalHandleSubmit();
    // NO hacer focus automáticamente porque causa parpadeo
    // El usuario ya está en el campo correcto cuando guarda con Ctrl+S
};

// 📄 Función para generar PDF de Nota Médica
const generateNotePdf = async () => {
    if (generatingPdf.value) return;

    if (!localNoteForm.value.id) {
        proxy.$notify(
            'Debes guardar la nota antes de generar el PDF',
            '',
            'warning',
            'ni ni-bell-55 ni-lg'
        );
        return;
    }

    try {
        generatingPdf.value = true;

        console.log('📄 Generando PDF de Nota Médica:', localNoteForm.value.id);

        const response = await api.post('/generate-medical-note-pdf', {
            note_id: localNoteForm.value.id
        });

        if (response.data && response.data.data && response.data.data.pdf_url) {
            const pdfUrl = response.data.data.pdf_url;

            // Agregar timestamp para evitar caché del navegador
            const urlWithTimestamp = pdfUrl.includes('?')
                ? `${pdfUrl}&t=${Date.now()}`
                : `${pdfUrl}?t=${Date.now()}`;

            // Abrir PDF en nueva pestaña
            window.open(urlWithTimestamp, '_blank');

            // Notificación de éxito
            proxy.$notify(
                response.data.message || 'Nota Médica generada exitosamente',
                '',
                'success',
                'ni ni-like-2 ni-lg'
            );
        } else {
            throw new Error('Respuesta inválida del servidor');
        }

    } catch (error) {
        console.error('❌ Error al generar PDF de Nota Médica:', error);

        const errorMessage = error.response?.data?.message ||
            error.message ||
            'No se pudo generar el PDF. Intenta nuevamente.';

        proxy.$notify(
            errorMessage,
            '',
            'danger',
            'ni ni-fat-remove ni-lg'
        );
    } finally {
        generatingPdf.value = false;
    }
};

// Funciones helper para mejorar la UX
const getTextLength = (html) => {
    if (!html) return 0;
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent.trim().length;
};

const getWordCount = (html) => {
    if (!html) return 0;
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const text = temp.textContent.trim();
    return text ? text.split(/\s+/).length : 0;
};

const fieldErrorFallbacks = {
    reason_for_visit: 'Indica en una frase el motivo principal de la consulta.',
    soap: 'Completa la evolución PSOAP para guardar la nota.',
    physical_examination: 'Describe los hallazgos clave de la exploración física.',
    laboratory_and_studies: 'Agrega los estudios solicitados y resultados relevantes.',
    diagnoses_clinical_problems: 'Especifica diagnósticos o problemas clínicos.'
};

const getErrorMessage = (error, fieldName = '') => {
    if (error) {
        if (Array.isArray(error)) return error[0];
        if (typeof error === 'string' && error.trim()) {
            if (fieldName === 'reason_for_visit' && error.toLowerCase().includes('motivo de visita')) {
                return 'Agrega el motivo de la visita (síntoma, control o estudio solicitado).';
            }
            return error;
        }
    }
    return fieldErrorFallbacks[fieldName] || 'Completa este campo.';
};

// ⌨️ Configurar atajos de teclado
const { isMac } = useKeyboardShortcuts({
    onSave: handleSubmit,
    onNew: () => {
        emit('reset-form');
        emit('update:activeTab', 'create');
    },
    onList: () => {
        // Ir a la pestaña de lista
        emit('update:activeTab', 'list');
    },
    onCancel: () => {
        // Solo cancelar si estamos editando, no cerrar la vista
        if (props.currentId && props.currentId > 0) {
            emit('update:activeTab', 'view');
        }
    }
});

// 💾 Configurar auto-guardado
const autosaveKey = computed(() => `medical-note-draft-${props.patientRecord?.id || 'temp'}`);
const fieldsToAutosave = [
    'reason_for_visit',
    'soap',
    'physical_examination',
    'laboratory_and_studies',
    'diagnoses_clinical_problems',
    'created'
];

const {
    lastSaveTime,
    hasUnsavedChanges,
    hasDraft,
    loadDraft,
    discardDraft,
    getFormattedSaveTime
} = useAutosave(
    localNoteForm,
    autosaveKey, // Pasar el computed directamente, no .value
    30000, // 30 segundos
    fieldsToAutosave
);

// Restaurar borrador
const handleRestoreDraft = () => {
    const draft = loadDraft();
    if (draft) {
        Object.assign(localNoteForm.value, draft);
        console.log('✨ Borrador restaurado exitosamente');
    }
};

// Descartar borrador
const handleDiscardDraft = () => {
    discardDraft();
    console.log('🗑️ Borrador descartado');
};

// Watch para limpiar errores cuando el usuario empiece a escribir
watch(
    () => localNoteForm.value.reason_for_visit,
    (newValue) => {
        if (newValue && props.errors.reason_for_visit) {
            emit('clear-field-error', 'reason_for_visit');
        }
    }
);

watch(
    () => localNoteForm.value.soap,
    (newValue) => {
        if (newValue && props.errors.soap) {
            emit('clear-field-error', 'soap');
        }
    }
);

watch(
    () => localNoteForm.value.physical_examination,
    (newValue) => {
        if (newValue && props.errors.physical_examination) {
            emit('clear-field-error', 'physical_examination');
        }
    }
);

watch(
    () => localNoteForm.value.laboratory_and_studies,
    (newValue) => {
        if (newValue && props.errors.laboratory_and_studies) {
            emit('clear-field-error', 'laboratory_and_studies');
        }
    }
);

watch(
    () => localNoteForm.value.diagnoses_clinical_problems,
    (newValue) => {
        if (newValue && props.errors.diagnoses_clinical_problems) {
            emit('clear-field-error', 'diagnoses_clinical_problems');
        }
    }
);

watch(
    () => localNoteForm.value.created,
    (newValue) => {
        if (newValue && props.errors.created) {
            emit('clear-field-error', 'created');
        }
    }
);

// Establecer autofocus en el campo motivo de consulta cuando se monta el componente
onMounted(() => {
    // Función helper para hacer scroll completo
    const scrollToTop = () => {
        // Scroll al window
        window.scrollTo({ top: 0, behavior: 'instant' });

        // Scroll al contenedor del formulario si existe
        if (formContainer.value) {
            formContainer.value.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
    };

    // Scroll inicial
    scrollToTop();

    nextTick(() => {
        scrollToTop();
    });
});

// Watch para scroll cuando cambie el modo de edición (cuando se selecciona una nota)
watch(() => props.currentId, (newId, oldId) => {
    // Solo hacer scroll si realmente cambió el ID (evitar el scroll inicial en 0)
    if (newId && newId > 0 && newId !== oldId) {
        // Función helper para scroll completo
        const scrollToTop = () => {
            window.scrollTo({ top: 0, behavior: 'instant' });

            if (formContainer.value) {
                formContainer.value.scrollIntoView({ behavior: 'instant', block: 'start' });
            }
        };

        // Scroll inmediato
        scrollToTop();

        // Scroll después de que el DOM se actualice
        nextTick(() => {
            scrollToTop();

            // Un último scroll para asegurar después del render
            setTimeout(() => {
                scrollToTop();
            }, 100);
        });
    }
});

// Watch adicional para cuando cambie el noteForm completo
watch(() => props.noteForm, (newForm, oldForm) => {
    // Detectar cambios significativos en el formulario
    const hasIdChanged = newForm?.id && newForm.id !== oldForm?.id;
    const hasReasonChanged = newForm?.reason_for_visit !== oldForm?.reason_for_visit;

    if (hasIdChanged || (hasReasonChanged && newForm?.id)) {
        // Función helper para scroll completo
        const scrollToTop = () => {
            window.scrollTo({ top: 0, behavior: 'instant' });

            if (formContainer.value) {
                formContainer.value.scrollIntoView({ behavior: 'instant', block: 'start' });
            }
        };

        // Scroll inmediato
        scrollToTop();

        // Scroll después del renderizado
        nextTick(() => {
            scrollToTop();

            // Un último scroll para asegurar después del render
            setTimeout(() => {
                scrollToTop();
            }, 100);
        });
    }
}, { deep: true });

</script>

<style scoped>
.modern-form-container {
    --accent: #3b82f6;
    --accent-strong: #2563eb;
    --accent-soft: #eff6ff;
    --border: #f1f5f9;
    --surface: #ffffff;
    --muted-surface: #f8fafc;
    --text: #334155;
    --muted-text: #64748b;
    --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    --shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    margin-bottom: 24px;
    background: var(--surface);
    border-radius: 16px;
    border: 1px solid var(--border);
    box-shadow: var(--shadow);
    overflow: hidden;
}

.form-header {
    background: white;
    border-bottom: 1px solid var(--border);
    padding: 1.5rem;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
}

.title-section {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.icon-wrapper {
    background: var(--accent-soft);
    border-radius: 12px;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    color: var(--accent);
}

.form-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text);
    letter-spacing: -0.025em;
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.id-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    background: var(--muted-surface);
    border: 1px solid var(--border);
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--muted-text);
}

.form-subtitle {
    margin: 0.25rem 0 0;
    color: var(--muted-text);
    font-size: 0.875rem;
}

.action-buttons {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.header-btn-primary {
    box-shadow: var(--shadow-sm) !important;
    font-weight: 500 !important;
    font-size: 0.875rem !important;
    padding: 0.625rem 1.25rem !important;
    border-radius: 8px !important;
    text-transform: none !important;
    background: var(--accent) !important;
    border: 1px solid var(--accent-strong) !important;
    color: white !important;
    transition: all 0.2s ease !important;
}

.header-btn-primary:hover {
    background: var(--accent-strong) !important;
    transform: translateY(-1px) !important;
}

.header-btn-secondary {
    box-shadow: none !important;
    padding: 0.5rem !important;
    border-radius: 8px !important;
    background: white !important;
    border: 1px solid var(--border) !important;
    color: var(--muted-text) !important;
    transition: all 0.2s ease !important;
}

.header-btn-secondary:hover {
    border-color: var(--accent) !important;
    color: var(--accent) !important;
    background: var(--accent-soft) !important;
}

.secondary-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
}

.modern-form {
    background: var(--surface);
    padding: 1.5rem;
}

.status-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1.5rem;
    background: var(--muted-surface);
    border-bottom: 1px solid var(--border);
    gap: 1rem;
}

.status-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.draft-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.75rem;
    background: white;
    border: 1px solid var(--border);
    border-radius: 9999px;
    color: var(--muted-text);
    font-size: 0.75rem;
    font-weight: 500;
}

.draft-badge i {
    color: var(--accent);
}

.draft-btn-restore,
.draft-btn-discard {
    padding: 0.25rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.75rem;
    transition: all 0.2s ease;
    border: none;
    background: transparent;
    color: var(--muted-text);
}

.draft-btn-restore:hover {
    color: var(--accent);
    background: var(--accent-soft);
}

.draft-btn-discard:hover {
    color: #ef4444;
    background: #fee2e2;
}

.keyboard-help-btn {
    padding: 0.5rem;
    background: white;
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--muted-text);
    cursor: pointer;
    font-size: 0.875rem;
    transition: all 0.2s ease;
}

.keyboard-help-btn:hover {
    border-color: var(--accent);
    color: var(--accent);
}

.form-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    border: 1px solid var(--border);
    box-shadow: var(--shadow-sm);
}

.section-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
    font-weight: 600;
    color: var(--text);
    font-size: 1rem;
}

.section-title i {
    color: var(--accent);
    font-size: 1.1rem;
}

.section-title.enhanced {
    background: transparent;
    border: none;
    border-bottom: 1px solid var(--border);
    padding: 0 0 1rem 0;
    margin: 0 0 1.5rem 0;
    border-radius: 0;
}

.form-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text);
    font-size: 0.875rem;
}

.form-label.modern {
    justify-content: space-between;
    margin-bottom: 0.75rem;
}

.label-content {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.label-content i {
    color: var(--muted-text);
    font-size: 0.875rem;
}

.required-badge {
    background: var(--accent-soft);
    color: var(--accent);
    font-size: 0.7rem;
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
    font-weight: 600;
}

.editor-wrapper {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid var(--border);
    background: white;
    transition: all 0.2s ease;
}

.editor-wrapper:focus-within {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-soft);
}

.editor-wrapper.has-error {
    border-color: #ef4444;
}

.error-message-modern {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    color: #ef4444;
    font-size: 0.875rem;
}

.help-text-modern {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    color: var(--muted-text);
    font-size: 0.875rem;
}

:deep(.ql-toolbar) {
    background: var(--muted-surface);
    border: none;
    border-bottom: 1px solid var(--border);
    padding: 0.75rem;
}

:deep(.ql-container) {
    border: none !important;
    font-size: 0.95rem;
    font-family: inherit;
}

:deep(.ql-editor) {
    min-height: 150px;
    padding: 1rem;
}

.editor-footer {
    background: var(--muted-surface);
    border-top: 1px solid var(--border);
    padding: 0.5rem 1rem;
}

.editor-stats {
    display: flex;
    gap: 1rem;
}

.stat-item {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    color: var(--muted-text);
}

.diagnoses-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.25rem;
}

.grid-item {
    background: var(--muted-surface);
    padding: 1.25rem;
    border-radius: 8px;
    border: 1px solid var(--border);
}

.notes-navigation {
    background: var(--muted-surface);
    border-radius: 12px;
    padding: 1rem;
    margin-top: 1.5rem;
    border: 1px solid var(--border);
}

.input-wrapper {
    position: relative;
}

.form-control.modern {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 0.95rem;
    transition: all 0.2s ease;
    background: white;
}

.form-control.modern:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-soft);
}

.success-indicator {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #10b981;
    font-size: 1.1rem;
}

.info-badge {
    background: var(--accent-soft);
    color: var(--accent);
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    font-size: 0.75rem;
    cursor: help;
}

@media (max-width: 768px) {
    .status-bar {
        flex-direction: column;
        gap: 1rem;
        padding: 1rem;
    }

    .status-left,
    .status-center,
    .status-right {
        width: 100%;
        justify-content: center;
    }

    .header-content {
        flex-direction: column;
        text-align: center;
    }

    .modern-form {
        padding: 1rem;
    }

    .diagnoses-grid {
        grid-template-columns: 1fr;
    }
}

/* Estilos para el botón PDF */
.pdf-btn {
    min-width: 42px;
    height: 42px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.pdf-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(233, 30, 99, 0.3);
}

.pdf-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.pdf-btn .fa-spinner {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>
